import { toI18n } from 'util/i18n';

import {
  ApplicationStage,
  ApplicationStatus,
  DBApplicationStatus,
} from './interfaces/IApplication';

export const HIRING_V2_SLICE_NAME = 'hiringV2';
export interface NewJobRequestParams {
  draftId?: number;
  jobId?: string;
  redirectUrl?: string;
  fromOnboarding?: boolean;
  role?: string;
}
export const HIRING_V2_BASE_URL = '/hiring/v2';
export const HIRING_V2_URLS = {
  DASHBOARD: `${HIRING_V2_BASE_URL}/dashboard`,
  JOB_POST: (id: string) => `${HIRING_V2_BASE_URL}/job_posts?id=${id}`,
  JOB_POSTS: `${HIRING_V2_BASE_URL}/job_posts`,
  JOB_REQUESTS: `${HIRING_V2_BASE_URL}/job_requests`,
  NEW_JOB_REQUESTS: ({
    draftId,
    jobId,
    redirectUrl,
    fromOnboarding,
    role,
  }: NewJobRequestParams) => {
    const params = new URLSearchParams();
    if (draftId) params.set('draft_id', draftId.toString());
    if (jobId) params.set('job_id', jobId);
    if (redirectUrl) params.set('redirect_url', redirectUrl);
    if (fromOnboarding) params.set('from_onboarding', 'true');
    if (role) params.set('role', role);
    params.set('fromJobPostFlow', 'true');
    const queryString = params.toString();
    return `${HIRING_V2_BASE_URL}/job_requests/new${
      queryString ? `?${queryString}` : ''
    }`;
  },
  REVIEW_APPLICANTS: `${HIRING_V2_BASE_URL}/review_applicants`,
  CANDIDATE_PROFILE: (id: string) =>
    `${HIRING_V2_BASE_URL}/review_applicants?profile=true&job_application_id=${id}`,
  SCREENING_AGENT_DEMO: `${HIRING_V2_BASE_URL}/screening_agent_demo`,
  SET_INTERVIEW_TIMES: `${HIRING_V2_BASE_URL}/set_interview_times`,
  REVIEW_BUSINESS_PROFILE: `${HIRING_V2_BASE_URL}/review_business_profile`,
};

export const JOB_REQUEST_STATUS = {
  ACTIVE: 'active',
  DRAFT: 'draft',
  PENDING_REVIEW: 'pending_review',
  FLAGGED: 'flagged',
  CLOSED: 'closed',
  ARCHIVED: 'archived',
  EXPIRED: 'expired',
} as const;

export enum AGG_JOB_REQUEST_STATUS {
  LIVE = 'live',
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  CLOSED = 'closed',
}

export enum INTERVIEW_STATUS {
  CONFIRMED = 'accepted',
  NOT_CONFIRMED = 'pending',
  NEW_TIME_PROPOSED = 'reschedule_requested',
}

export const DAY_NUMBER_MAPPING: Record<string, string> = {
  0: toI18n('hiring.v2.job_request.job_description_page.job_details.monday'),
  1: toI18n('hiring.v2.job_request.job_description_page.job_details.tuesday'),
  2: toI18n('hiring.v2.job_request.job_description_page.job_details.wednesday'),
  3: toI18n('hiring.v2.job_request.job_description_page.job_details.thursday'),
  4: toI18n('hiring.v2.job_request.job_description_page.job_details.friday'),
  5: toI18n('hiring.v2.job_request.job_description_page.job_details.saturday'),
  6: toI18n('hiring.v2.job_request.job_description_page.job_details.sunday'),
};

export const DAY_NUMBER_MAPPING_REVERSE: Record<string, string> = {
  monday: '0',
  tuesday: '1',
  wednesday: '2',
  thursday: '3',
  friday: '4',
  saturday: '5',
  sunday: '6',
};

export const DEFAULT_JOB_ROLES = [
  'Server',
  'Cashier',
  'Bartender',
  'Barista',
  'Cook',
];

export const HOMEBASE_BACKGROUND_CHECK_URL = '/hr_center/background_checks';

export const HIRING_V2_BOOK_A_CALL_URL =
  'https://calendly.com/hiring-team-ieau/call-with-homebase-hiring';

export const HIRING_V2_GIVE_FEEDBACK_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSf1NRKKy0IEfpeh8F_f0YLxPlpCqInrifyuoIcHL43_1QSx9A/viewform?usp=header';

export const APPLICATION_STAGE = {
  ai_screener: 'AI Screener',
  interview: 'Interview',
  decline: 'Decline',
  hire: 'Hire',
  // @TODO: Uncomment this when the statuses for the stages are available on the BE
  // maybe_later: 'Maybe Later',
} as const;

export const APPLICATION_STATUS: Record<ApplicationStatus, string> = {
  needs_review: 'Needs Review',
  has_completed_screener: 'Has Completed Screener',
  booking_link_sent: 'Booking Link Sent',
  interview_scheduled: 'Interview Scheduled',
  interview_reschedule_request: 'Interview Reschedule Request',
  needs_decision: 'Needs Decision',
  added_to_team: 'Added to Team',
  sent_decline_note: 'Sent Decline Note',
  sent_note: 'Sent Note',
} as const;

export const APPLICATION_STATUS_TO_DB_STATUS_MAP: Record<
  keyof typeof APPLICATION_STATUS,
  DBApplicationStatus[]
> = {
  needs_review: ['new'],
  has_completed_screener: [],
  booking_link_sent: ['interview_schedule_link_sent'],
  interview_scheduled: ['oam_confirmed_interview'],
  interview_reschedule_request: ['interview_schedule_link_sent'],
  added_to_team: ['added_to_team'],
  sent_decline_note: ['interview_declined', 'not_interested'],
  sent_note: ['not_interested'],
  needs_decision: ['candidate_requested_interview'],
};

export const STATUSES_IN_STAGE_MAP: Record<
  ApplicationStage,
  (keyof typeof APPLICATION_STATUS)[]
> = {
  ai_screener: ['needs_review', 'has_completed_screener'],
  interview: ['interview_scheduled', 'interview_reschedule_request'],
  hire: ['added_to_team'],
  decline: ['sent_decline_note'],
  maybe_later: [],
};

export const V2_DASHBOARD_TOUR_QUERY_PARAM = 'hiring_v2_dashboard_tour';

export const APPLICATION_SOURCE_MAP = {
  // Main Job Boards
  indeed: 'Indeed',
  zip_recruiter: 'ZipRecruiter',
  glassdoor: 'Glassdoor',
  craigslist: 'Craigslist',
  google: 'Google Jobs',
  handshake: 'Handshake',
  homebase: 'Homebase',
  twitter: 'X',
  linked_in: 'LinkedIn',
  talroo: 'Talroo',
  facebook: 'Facebook',
  facebook_jobs: 'Facebook Jobs',
  facebook_email: 'Facebook Email',
  instagram: 'Instagram',

  // Walk-ins
  qr_poster: 'Walk-ins',
  walk_in: 'Walk-ins',
  text_to_apply: 'Walk-ins',

  // Referral
  homebase_employee_referral: 'Referral',
  referral_message: 'Referral',

  // Others
  steady: 'Homebase',
  generic_feed: 'Homebase',
} as const;

export const HIRING_V2_MEMBER_ADDED_MODAL = 'HIRING_V2_MEMBER_ADDED_MODAL';
export const HIRING_V2_JOB_BOOST_MODAL = 'HIRING_V2_JOB_BOOST_MODAL';
export const HIRING_V2_JOB_BOOST_NO_BOOST_MODAL =
  'HIRING_V2_JOB_BOOST_NO_BOOST_MODAL';
export const HIRING_V2_SKIP_ADD_MEMBER_TO_TEAM_MODAL =
  'HIRING_V2_SKIP_ADD_MEMBER_TO_TEAM_MODAL';
export const HIRING_V2_SHARE_OPPORTUNITY_MODAL =
  'HIRING_V2_SHARE_OPPORTUNITY_MODAL';
export const HIRING_V2_STOP_JOB_POST_MODAL = 'HIRING_V2_STOP_JOB_POST_MODAL';
export const HIRING_V2_DELETE_DRAFT_MODAL = 'HIRING_V2_DELETE_DRAFT_MODAL';
export const HIRING_V2_SCHEDULE_CALL_MODAL = 'HIRING_V2_SCHEDULE_CALL_MODAL';
export const HIRING_V2_START_BACKGROUND_CHECK_MODAL =
  'HIRING_V2_START_BACKGROUND_CHECK_MODAL';
export const HIRING_V2_UPGRADE_MODAL = 'HIRING_V2_UPGRADE_MODAL';
export const HIRING_V2_ADD_TEAM_MODAL = 'HIRING_V2_ADD_TEAMS_MODAL';
export const HIRING_V2_TRIAL_START_MODAL = 'HIRING_V2_TRIAL_START_MODAL';
export const HIRING_V2_TRIAL_END_MODAL = 'HIRING_V2_TRIAL_END_MODAL';

// Nylas Scheduler
export const HIRING_V2_NYLAS_SCHEDULE_INTERVIEW_TOP_MATCH_MODAL =
  'HIRING_V2_NYLAS_SCHEDULE_INTERVIEW_TOP_MATCH_MODAL';
export const HIRING_V2_NYLAS_LINK_TO_SCHEDULE_INTERVIEW_MODAL =
  'HIRING_V2_NYLAS_LINK_TO_SCHEDULE_INTERVIEW_MODAL';
export const HIRING_V2_NYLAS_DECLINE_INTERVIEW_MODAL =
  'HIRING_V2_NYLAS_DECLINE_INTERVIEW_MODAL';
export const HIRING_V2_VERIFY_CANCEL_INTERVIEW_TIMES_MODAL =
  'HIRING_V2_VERIFY_CANCEL_INTERVIEW_TIMES_MODAL';
export const HIRING_V2_SHARE_AND_BOOST_MODAL =
  'HIRING_V2_SHARE_AND_BOOST_MODAL';
export const HIRING_V2_DECLINE_CANDIDATE_MODAL =
  'HIRING_V2_DECLINE_CANDIDATE_MODAL';
export const HIRING_V2_RESCHEDULE_REQUESTS_MODAL =
  'HIRING_V2_RESCHEDULE_REQUESTS_MODAL';
